import React, { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  Drawer,
  EmptyState,
  Input,
  Switch,
  Textarea,
  useToast,
} from '../../../ui/components';
import { IconButton } from '../../../ui/components/Button';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';
import { suppliersApi } from '../suppliersApi';

const EMPTY = { name: '', title: '', role: '', email: '', phone: '', isPrimary: false, notes: '' };

const normalize = (c) => (c
  ? {
    name: c.name ?? '', title: c.title ?? '', role: c.role ?? '', email: c.email ?? '',
    phone: c.phone ?? '', isPrimary: !!c.isPrimary, notes: c.notes ?? '',
  }
  : { ...EMPTY });

function ContactForm({ initial, submitting, onSubmit, onCancel }) {
  const t = useT();
  const [form, setForm] = useState(() => normalize(initial));
  const [errors, setErrors] = useState({});
  useEffect(() => { setForm(normalize(initial)); setErrors({}); }, [initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = t('Name is required.');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = t('Enter a valid email.');
    setErrors(next);
    if (Object.keys(next).length) return;
    onSubmit({
      name: form.name.trim(),
      title: form.title.trim() || null,
      role: form.role.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      isPrimary: form.isPrimary,
      notes: form.notes.trim() || null,
    });
  };

  return (
    <form className="sp-form" onSubmit={submit}>
      <Input label={t('Name')} value={form.name} onChange={set('name')} error={errors.name} autoFocus />
      <div className="sp-form__row">
        <Input label={t('Title')} value={form.title} onChange={set('title')} placeholder={t('Account Manager')} />
        <Input label={t('Role')} value={form.role} onChange={set('role')} placeholder={t('Sales, AP…')} />
      </div>
      <div className="sp-form__row">
        <Input label={t('Email')} type="email" value={form.email} onChange={set('email')} error={errors.email} />
        <Input label={t('Phone')} value={form.phone} onChange={set('phone')} />
      </div>
      <Switch checked={form.isPrimary} onChange={(v) => setForm((f) => ({ ...f, isPrimary: v }))} label={t('Primary contact')} />
      <Textarea label={t('Notes')} value={form.notes} onChange={set('notes')} rows={2} autoGrow maxRows={6} />
      <div className="sp-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>{t('Cancel')}</Button>
        <Button type="submit" variant="primary" loading={submitting}>{t('Save contact')}</Button>
      </div>
    </form>
  );
}

export default function ContactsTab({ supplier, onChanged }) {
  const toast = useToast();
  const t = useT();
  const contacts = supplier.contacts || [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (c) => { setEditing(c); setDrawerOpen(true); };
  const close = () => { if (!submitting) { setDrawerOpen(false); setEditing(null); } };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      const res = editing
        ? await suppliersApi.updateContact(supplier.id, editing.id, payload)
        : await suppliersApi.addContact(supplier.id, payload);
      onChanged(res);
      toast.success(editing ? t('Contact updated') : t('Contact added'));
      setDrawerOpen(false);
      setEditing(null);
    } catch (err) {
      toast.error(t('Could not save contact'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await suppliersApi.removeContact(supplier.id, deleteTarget.id);
      onChanged(res);
      toast.success(t('Contact removed'));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(t('Could not remove contact'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="sp-panel">
      <div className="sp-section-head">
        <h2 className="sp-section-head__title">{t('Contacts')}</h2>
        <Button variant="primary" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={openCreate}>{t('Add contact')}</Button>
      </div>

      {contacts.length === 0 ? (
        <EmptyState
          icon={<Icon.User size={28} />}
          title={t('No contacts yet')}
          description={t('Add the people you deal with at this supplier.')}
          action={<Button variant="primary" leadingIcon={<Icon.Plus size={15} />} onClick={openCreate}>{t('Add contact')}</Button>}
        />
      ) : (
        <div className="sp-list">
          {contacts.map((c) => (
            <div key={c.id} className="sp-item">
              <div className="sp-item__main">
                <span className="sp-item__title">
                  {c.name}
                  {c.isPrimary && <Badge tone="brand" dot>{t('Primary')}</Badge>}
                  {c.title && <Badge tone="neutral">{c.title}</Badge>}
                  {c.role && <span className="sp-muted" style={{ fontSize: 'var(--text-xs)' }}>{c.role}</span>}
                </span>
                <span className="sp-item__meta">
                  {c.email && <a href={`mailto:${c.email}`}><Icon.Mail size={13} /> {c.email}</a>}
                  {c.phone && <a href={`tel:${c.phone}`}><Icon.Phone size={13} /> {c.phone}</a>}
                </span>
                {c.notes && <span className="sp-muted" style={{ fontSize: 'var(--text-sm)' }}>{c.notes}</span>}
              </div>
              <div className="sp-item__actions">
                <IconButton aria-label={t('Edit contact')} variant="ghost" size="icon-sm" onClick={() => openEdit(c)}>
                  <Icon.Pencil size={15} />
                </IconButton>
                <IconButton aria-label={t('Remove contact')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(c)}>
                  <Icon.Trash size={15} />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer open={drawerOpen} onClose={close} title={editing ? t('Edit contact') : t('Add contact')} size="md">
        <ContactForm initial={editing} submitting={submitting} onSubmit={handleSubmit} onCancel={close} />
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={handleDelete}
        title={t('Remove contact')}
        description={deleteTarget ? t('Remove {name}?', { name: deleteTarget.name }) : ''}
        confirmLabel={t('Remove')}
        tone="danger"
        loading={deleting}
      />
    </div>
  );
}
